import React, { useState } from "react";
import { FormFeedback, Tooltip } from "reactstrap";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import DOMPurify from "dompurify"; // For sanitizing HTML
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"; // WYSIWYG editor styles

const WYSIWYGEditorInput = ({
    label = "Enter Content", // Default label
    placeholder = "Enter your text here", // Default placeholder
    required = false, // Required field
    layout = "below", // Layout option (below or inline)
    value = "", // Initial value
    name="",
    onChange = () => {}, // Function to handle content change
    maxLength = 1000, // Maximum character limit
    requiredErrorMessage = "This field is required.",
    invalidContentErrorMessage = "Invalid content detected.",
    maxLengthErrorMessage = `You can enter up to ${maxLength} characters only.`,
    showHelp = false, // Show help tooltip
    helpText = "Use the editor for text formatting.",
    customToolbarOptions = {}, // To customize toolbar appearance
    sanitizeHtml = true, // Whether to sanitize HTML
    allowedTags = ["b", "i", "u", "a", "p"], // Customizable allowed tags for sanitization
}) => {
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [errors, setErrors] = useState({});
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [charCount, setCharCount] = useState(0);

    // Handle editor state change and validation
    const handleEditorChange = (newState) => {
        const rawContent = draftToHtml(convertToRaw(newState.getCurrentContent()));
        const cleanContent = sanitizeHtml ? DOMPurify.sanitize(rawContent, { ALLOWED_TAGS: allowedTags }) : rawContent;
        const contentLength = cleanContent.replace(/<[^>]*>?/gm, "").length; // Counting only the text

        // Update character count
        setCharCount(contentLength);

        // Validation for max length
        if (contentLength > maxLength) {
            setErrors({ maxLength: maxLengthErrorMessage });
        } else {
            setErrors({});
        }

        setEditorState(newState);
        onChange(cleanContent); // Pass sanitized or raw content as value
    };

    // Validation for required field
    const validateContent = () => {
        const rawContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        const cleanContent = sanitizeHtml ? DOMPurify.sanitize(rawContent, { ALLOWED_TAGS: allowedTags }) : rawContent;

        if (required && cleanContent.trim() === "") {
            setErrors({ required: requiredErrorMessage });
            return false;
        }

        if (cleanContent.length > maxLength) {
            setErrors({ maxLength: maxLengthErrorMessage });
            return false;
        }

        setErrors({});
        return true;
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
            <label htmlFor="editor" className="d-flex" style={{ width: layout === "inline" ? "20%" : "100%" }}>
                {label}
                {required && <span className="text-danger ml-1">*</span>}
                {showHelp && (
                    <span id="helpTooltip" className="px-2">
                        <i className="ri-question-line" style={{ cursor: "pointer", fontSize: "16px" }}></i>
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target="helpTooltip"
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100 border">
                <Editor
                    editorState={editorState}
                    toolbarClassName="toolbar-class"
                    wrapperClassName="wrapper-class"
                    editorClassName="editor-class"
                    placeholder={placeholder}
                    name={name}
                    toolbar={{
                        options: ["inline", "fontSize", "list", "textAlign", "link", "image", "history"],
                        inline: { options: ["bold", "italic", "underline"] },
                        textAlign: { options: ["left", "center", "right", "justify"] },
                        list: { options: ["unordered", "ordered"] },
                        ...customToolbarOptions, // Allow customization of toolbar
                    }}
                    onEditorStateChange={handleEditorChange}
                />
                <div className="d-flex justify-content-between">
                    <span>{charCount}/{maxLength} characters</span>
                </div>

                {/* Error feedback */}
                {errors.required && (
                    <FormFeedback type="invalid" className="d-block">
                        {errors.required}
                    </FormFeedback>
                )}
                {errors.maxLength && (
                    <FormFeedback type="invalid" className="d-block">
                        {errors.maxLength}
                    </FormFeedback>
                )}
                {errors.invalidContent && (
                    <FormFeedback type="invalid" className="d-block">
                        {invalidContentErrorMessage}
                    </FormFeedback>
                )}
            </div>
        </div>
    );
};

export default WYSIWYGEditorInput;
